import React, { useCallback, useRef, useImperativeHandle, RefObject } from 'react';
import Dropzone, { DropzoneRef, DropzoneRootProps, FileRejection, ErrorCode } from 'react-dropzone';
import { View } from 'react-native';
import { STRING_CONSTANTS } from './MB_ResourcePickerConstants';
import { MB_ResourcePickerGetAllowedFileFormats, MB_RESOURCE_PICKER_TYPE } from './MB_ResourcePickerUtils';

// TODO: Dropzone stuck on version ^11

interface MB_ResourcePickerDropzoneForWebRef {
  openFileSelector: () => void;
}

interface IMB_ResourcePickerProps {
  /**
   * The ref to the `MB_ResourcePickerDropzoneForWeb` component.
   */
  MB_Ref?: RefObject<MB_ResourcePickerDropzoneForWebRef | null>;

  /**
   * Children components.
   */
  children: React.ReactNode;

  /**
   * The type of the resource picker. This determines what kind of
   * resource the user can pick.
   */
  resourcePickerType: MB_RESOURCE_PICKER_TYPE;

  /**
   * The callback to call when a resource is picked.
   */
  onResourcePicked: ({ blob, uri }: { blob: Blob; uri: string }) => void;

  /**
   * The callback to call when an error occurs.
   */
  onError: (errorMessage: string) => void;

  /**
   * Whether to allow the user to click on the dropzone to open the file selector.
   */
  allowOnClickOpenFiles?: boolean;

  /**
   * The allowed file extensions. In format `'.jpeg,.jpg,.png'`.
   */
  allowedFileExtensions?: string;
}

const MB_ResourcePickerDropzoneForWeb = ({
  MB_Ref,
  children,
  resourcePickerType,
  onResourcePicked,
  onError,
  allowOnClickOpenFiles,
  allowedFileExtensions,
}: IMB_ResourcePickerProps) => {
  const dropzoneRef = useRef<DropzoneRef>(null);

  useImperativeHandle(MB_Ref, () => ({
    openFileSelector: () => {
      dropzoneRef.current?.open();
    },
  }));

  const onDrop = useCallback(
    (acceptedFiles: any[], fileRejections: FileRejection[]) => {
      try {
        const rejectionReasonsArrayOfArrays = fileRejections.map(rejection => rejection.errors.map(error => error.code));
        const rejectionReasons = [...new Set(rejectionReasonsArrayOfArrays.flat())];

        if (rejectionReasons.length > 0 || acceptedFiles.length !== 1) {
          let errorString = STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN;
          if (rejectionReasons.includes(ErrorCode.TooManyFiles)) {
            errorString = STRING_CONSTANTS.IMAGE_UPLOADER_ERROR_TOO_MANY_FILES;
          } else if (rejectionReasons.includes(ErrorCode.FileInvalidType)) {
            errorString =
              STRING_CONSTANTS.IMAGE_UPLOADER_ERROR_INCORRECT_FORMAT +
              `\nPlease submit a file with one of these formats\n${allowedFileExtensions ?? MB_ResourcePickerGetAllowedFileFormats(resourcePickerType)}`;
          }

          console.log('Error in dropzone', rejectionReasons);
          onError(errorString);
          return;
        }

        onResourcePicked({ blob: acceptedFiles[0], uri: URL.createObjectURL(acceptedFiles[0]) });
      } catch (error) {
        console.log('Error in dropzone', error);
        onError(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
      }
    },
    [allowedFileExtensions, onError, onResourcePicked, resourcePickerType]
  );

  return (
    <View style={{ width: '100%', height: '100%', position: 'absolute', zIndex: 1 }}>
      <Dropzone onDrop={onDrop} multiple={false} accept={allowedFileExtensions ?? MB_ResourcePickerGetAllowedFileFormats(resourcePickerType)} ref={dropzoneRef}>
        {({ getRootProps, getInputProps }) => {
          const additionalDropdownDivProps: DropzoneRootProps = {
            onFocus: undefined,
            onClick: allowOnClickOpenFiles ? () => dropzoneRef.current?.open() : undefined,
            onBlur: undefined,
          };

          return (
            <section>
              <div {...getRootProps()} {...additionalDropdownDivProps} style={dropzoneDivStyle}>
                {children}
              </div>
              <input {...getInputProps()} />
            </section>
          );
        }}
      </Dropzone>
    </View>
  );
};

export { MB_ResourcePickerDropzoneForWeb };
export type { MB_ResourcePickerDropzoneForWebRef };

const dropzoneDivStyle: any = {
  display: 'flex',
  backgroundColor: 'transparent',
  justifyContent: 'center',
  position: 'absolute',
  width: '100%',
  height: '100%',
};
